import type { InferType } from "yup";
import { array, boolean, object, string } from "yup";

import type {
  Section12Status,
  Team,
  UserOrTeamSearchResult,
  UserSearchResult,
} from "@thalamos/aspire-common";
import { nonEmptyString } from "@thalamos/aspire-common";

export type Result = {
  teamId: string;
  // A user search result with no team context has no team type.
  teamType?: Team["type"];
  email: string;
  name: string;
  teamName?: string;
  contexts?: UserSearchResult["contexts"];
  assignedUserId?: string;
  guestInviteAccreditation: string;
  isTrainingOrganisation: boolean;
  entityType: "team" | "user";
  dataControllerId?: string | null;
  dataControllerName?: string | null;
  gmcNumber?: string | null;
  section12Status?: Section12Status;
};

export type NewResultWithoutAccreditation = Omit<
  Result,
  "guestInviteAccreditation"
>;

export const newFormAssigneeData = object({
  type: string().oneOf(["full-user", "guest"]),
  teamId: string(),
  assignedUserId: string(),
  email: nonEmptyString
    .email("Please enter a valid email")
    .required("Please enter an email"),
  name: nonEmptyString.required("Please enter a name"),
  guestInviteAccreditation: nonEmptyString.required(
    "Please confirm who you are sending this invite to",
  ),
  isTrainingOrganisation: boolean(),
  selectedTeam: string().when("userMembershipContext", {
    is: (contexts: UserSearchResult["contexts"]) => contexts.length > 1,
    then: (schema) => schema.required("Please select a team"),
    otherwise: (schema) => schema.optional(),
  }),
  userMembershipContext: array()
    .of(
      object({
        id: string(),
        email: string(),
        name: string(),
        role: string(),
        teamType: string(),
        isTrainingOrganisation: boolean(),
        dataControllerId: string().nullable(),
        dataControllerName: string().nullable(),
      }),
    )
    .default([]),

  teamName: string(),
  entityType: string(),
  teamType: string(),
  dataControllerId: string().nullable(),
  dataControllerName: string().nullable(),
  action: string().optional(),
  // Set when this recipient was chosen from the mental health care provider list,
  // so the selection can be persisted for reporting.
  mentalHealthCareProviderId: string().optional(),
});

export type FormDetails = {
  template: { id: string; version: string };
  part: number;
};

export type FormikNewFormAssigneeValues = {
  values: InferType<typeof newFormAssigneeData>;
  isSubmitting: boolean;
  submitForm: () => void;
  setValues: (values: any) => void;
  setTouched: (touched: any) => void;
};

export type FormikInitialValues = {
  type: string;
  teamId: string;
  email: string;
  name: string;
  guestInviteAccreditation: {};
  isTrainingOrganisation: boolean;
  selectedTeam: string;
  dataControllerId: string | null;
  dataControllerName: string | null;
};

interface AnsweredAssignmentSearch {
  status: 200;
  data: { results: UserOrTeamSearchResult[] };
}

/** A search may resolve with a refusal rather than reject, and a 200 says only that something answered, not that the search did, so no body is trusted to hold results until checked */
export type AssignmentSearchResponse =
  AnsweredAssignmentSearch | { status: number; data: unknown };

export function isAnsweredSearch(
  response: AssignmentSearchResponse,
): response is AnsweredAssignmentSearch {
  const { status, data } = response;
  return (
    status === 200 &&
    typeof data === "object" &&
    data !== null &&
    "results" in data &&
    Array.isArray(data.results)
  );
}
